import { Component, OnInit, HostBinding, Input, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { AttachmentStore } from '../service/attachment/attachment.store';
import { Attachment } from '../models/attachment';
import { List } from 'immutable';
import { OnyxEvent } from '../models/event';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { ConfirmationModalComponent } from '../../shared/confirmation-modal/confirmation-modal.component';
import { Observable } from 'rxjs/Observable';
import { AuthenticationStore } from '../../permission/services/authentication.store';
import { EnvironmentConfiguration } from '../../environment/services/environment.configuration';
import { User } from '../../auth/models/User';
import { Party } from '../models/party';
import { CommissionStore } from '../../payment/service';
import { ReservationStatusCode } from '../models/reservation-status-code.enum';

@Component({
  selector: 'pioneer-pickup-report-attachment',
  templateUrl: './pickup-report-attachment.component.html',
  styleUrls: ['./pickup-report-attachment.component.css']
})
export class PickupReportAttachmentComponent implements OnInit {
  @HostBinding('class.margin-content') true;

  @ViewChild('file') fileInput: ElementRef;

  @ViewChild(ConfirmationModalComponent)
  public confirmationModal: ConfirmationModalComponent;

  onyxEvent1: OnyxEvent;

  @Input()
  set event(event: OnyxEvent) {
    this.onyxEvent1 = event;
  }

  @Input() vendor: Party;

  @Output()
  public currentAttachment = new EventEmitter();

  /**
   * Used to determine if the upload bar should be shown while an attachment is uploading.
   * Determines animation delay before attachment list is refreshed.
   * @type {boolean}
   */
  isUploadInProgress = false;

  /**
   * Set when attachment delete starts.
   * Determines animation length before attachment list is refreshed.
   * @type {boolean}
   */
  isDeleteInProgress = false;

  /**
   * Sets css class on newest item.
   * @type {boolean}
   */
  isNewAttachment = false;

  /**
   * Set when some attachment is uploaded.
   * Disable button for upload more than 1 file.
   * @type {boolean}
   */
  containFile = false;

  /**
   * Sets css discarded class on deleted items.
   */
  removedAttachments: Set<string>;

  /**
   * Set when delete is initiated.
   * Since we're using the confirmation modal to confirm the delete,
   * we have to keep track of which attachment is being deleted.
   */
  attachmentToRemove: Attachment;

  startDate = new Date();

  fileTypes = this.environment.paymentActionReport.acceptedFileTypes;

  maxFileSize = this.environment.paymentActionReport.maxFileSize * 1024 * 1024;

  attachments = List<Attachment>([]);

  private user: User;

  constructor(private commissionStore: CommissionStore,
              private flashMessagesService: FlashMessagesService,
              private authStore: AuthenticationStore,
              private environment: EnvironmentConfiguration) {
  }

  ngOnInit() {
    this.removedAttachments = new Set();

    if (!this.onyxEvent1) {
      throw new Error(`An event is required to create the "pioneer-attachement" component`);
    }

    if (!this.vendor) {
      throw new Error(`A vendor is required to create the "pioneer-attachement" component`);
    }

    this.commissionStore.attachments.subscribe(attachments => {
      // No animation delay needed if we're not deleting or uploading an attachment.
      let animationDelay = 0;

      if (this.isDeleteInProgress) {
        animationDelay = 500;
      } else if (this.isUploadInProgress) {
        animationDelay = 1000;
      }
      this.containFile = attachments.length > 0;
      this.currentAttachment.emit(attachments);

      setTimeout(() => {
        this.attachments = attachments;

        this.isNewAttachment = this.isUploadInProgress;

        // When attachments are refreshed, "reset" page (nothing uploading or being deleted)
        this.isUploadInProgress = false;
        this.isDeleteInProgress = false;
        this.removedAttachments = new Set();
      }, this.getAnimationDelay(animationDelay, this.startDate));
    });

    this.authStore.user.take(1).subscribe((user: User) => {
      this.user = user;
    });
  }

  uploadFile(inputEvent): void {
    this.startDate = new Date();
    this.isUploadInProgress = true;

    if (!inputEvent || !inputEvent.srcElement || !inputEvent.srcElement.files || inputEvent.srcElement.files.size === 0) {
      this.handleUploadError('MESSAGES.ERROR.UPLOAD_ATTACHMENT');
      return;
    }

    let file = inputEvent.srcElement.files[0];
    let newName = 'PICKUP_REPORT_' + file.name;

    let fileRenamed = this.commissionStore.getFile(file, newName);
    this.clearFileInput();

    if (fileRenamed.size > this.maxFileSize) {
      this.handleUploadError('MESSAGES.ERROR.ATTACHMENT_TOO_LARGE');
      return;
    }

    if (this.environment.paymentActionReport.acceptedFileTypes.indexOf(fileRenamed.type) < 0) {
      this.handleUploadError('MESSAGES.ERROR.ATTACHMENT_UNSUPPORTED_FILE_TYPE_PAYMENT_ACTIONS');
      return;
    }

    this.commissionStore.addAttachment(this.onyxEvent1.eventId, this.vendor.eventPartyId, fileRenamed, this.isMultiVendor())
      .catch((error: any) => {
        this.flashMessagesService.error('MESSAGES.ERROR.UPLOAD_ATTACHMENT', `Error uploading attachment: ${error}`);
        this.isUploadInProgress = false;
        this.isNewAttachment = false;
        return Observable.throw(error);
      }).subscribe();
  }

  openFile(attachment: Attachment): void {
    this.commissionStore.getAttachment(attachment.uniqueId)
      .subscribe((response: any) => {
      }, (error: any) => {
        this.flashMessagesService.error('MESSAGES.ERROR.DOWNLOAD_ATTACHMENT', `Error retrieving attachments; ${error}`);
        return Observable.throw(error);
      });
  }

  /**
   * Deletes attachment as well as triggers deletion animation.
   * @param attachment
   * @returns {Observable<R|T>}
   */
  deleteFile(attachment: Attachment): void {
    this.startDate = new Date();
    this.isDeleteInProgress = true;

    // This triggers the animation on the specific item.
    this.removedAttachments.add(attachment.uniqueId);

    this.commissionStore.deleteAttachment(this.onyxEvent1.eventId, this.vendor.eventPartyId,
      attachment.uniqueId, this.isMultiVendor())
      .subscribe(
        (response: any) => {
        },
        (error: any) => {
          this.flashMessagesService.error('MESSAGES.ERROR.UPLOAD_ATTACHMENT', `Error uploading attachment: ${error}`);
          this.isUploadInProgress = false;
          this.isNewAttachment = false;
          return Observable.throw(error);
        });
  }

  confirmBeforeDelete(attachment: Attachment) {
    this.attachmentToRemove = attachment;
    this.confirmationModal.open();
  }

  public confirmHandler(value) {
    if (value) {
      this.deleteFile(this.attachmentToRemove);
    }

    this.attachmentToRemove = null;
  }

  getAnimationDelay(delayMillis: number, startDate: Date) {
    let animationDelay = delayMillis - (new Date().valueOf() - startDate.valueOf());
    return animationDelay > 0 ? animationDelay : 0;
  }

  getFileExtension(filename: string): string {
    if (!filename) {
      return '';
    }

    let filenameParts = filename.split('.');
    return filenameParts[filenameParts.length - 1];
  }

  handleUploadError(errorMessage: string, logMessage: string = null) {
    this.isUploadInProgress = false;
    this.flashMessagesService.error(errorMessage, logMessage);
  }

  /**
   * Returns true if the user has permissions to delete the attachment
   *
   * @param attachment the file to check
   * @returns `true` if both conditions are met; `false` otherwise
   */
  canDeleteFile(attachment: Attachment): boolean {
    if (this.authStore.hasPermissionCommission('DeleteFiles')) {
      return true;
    }
    if (this.authStore.isSupportUser()) {
      return true;
    }
    if (this.isMultiVendor() && this.authStore.matchUserType(attachment.creatorType)) {
      return true;
    }
    if (this.authStore.isPropertyUser() && attachment.eventPartyCreatorId === this.vendor.eventPartyId) {
      return true;
    }
    return false;
  }

  clearFileInput(): void {
    this.fileInput.nativeElement.value = null;
  }

  isMultiVendor() {
    return this.authStore.hasPermissionCommission('MultiVendor');
  }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/pickup-report-attachment/pickup-report-attachment.component.ts